'use client';

import { useIntl } from 'react-intl';

import { Button, type ButtonProps } from '@packages/shared/src/ui/Button/Button';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';

import { addToBasketMessages } from '../../messages';

export type AddToBasketButtonProps = Pick<
  ButtonProps,
  'size' | 'variant' | 'color' | 'disabled' | 'onClick'
> & {
  isAvailable: boolean;
  isLoading: boolean;
  buttonText?: string | null;
  endIconElement?: React.ReactNode;
  className?: string;
  iconClassName?: string;
};

/**
 * Renders the product purchase action with its availability and loading state.
 *
 * @param props - The button props.
 * @param props.isAvailable - Whether the product can be added to the basket.
 * @param props.isLoading - Whether the add-to-basket request is in progress.
 * @param props.buttonText - Overrides the availability-based button label ("null" means there should be no button text -> no fallback to the default button text).
 * @param props.endIconElement - Overrides the default cart or loading icon.
 * @param props.className - Extends the button styles.
 * @param props.iconClassName - Extends the default icon styles.
 * @returns The add-to-basket button.
 */
export const AddToBasketButton = ({
  size,
  variant,
  color,
  disabled,
  onClick,
  isAvailable,
  isLoading,
  buttonText,
  endIconElement,
  className,
  iconClassName,
}: AddToBasketButtonProps) => {
  const { formatMessage } = useIntl();

  let finalButtonText = buttonText;
  // only set the default button text if it hasn't been explicitly set to null
  if (finalButtonText !== null && !finalButtonText) {
    finalButtonText = isAvailable
      ? formatMessage(addToBasketMessages.available)
      : formatMessage(addToBasketMessages.soldOut);
  }
  let finalEndIconElement = endIconElement;
  if (finalEndIconElement === undefined) {
    finalEndIconElement = isLoading ? (
      <CircularProgress className={`text-color-secondary ${iconClassName ?? ''}`} />
    ) : (
      <span className={cn('icon-emp-cart text-color-primary-contrasttext size-4', iconClassName)} />
    );
  }

  return (
    <Button
      className={cn('w-full whitespace-nowrap', className)}
      onClick={onClick}
      size={size}
      variant={variant}
      color={color}
      disabled={disabled || !isAvailable || isLoading}
    >
      {buttonText !== null && finalButtonText}
      {finalEndIconElement}
    </Button>
  );
};
